/* this might become a server component in the future. It depends on ComponentRecognizer */
'use client';

import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { classes } from '../../utils/classes';
import { CategoryTeaserItem } from './CategoryTeaserItem.client';
import type { CategoryTeaserItemType, CategoryTeaserType } from './types';

/**
 * The CategoryTeaser component provides a flexible and customizable layout for displaying a series of teaser items.
 * It allows CMS editors to easily update the home pages and promotional landing pages of web stores.
 * The component supports various layout options such as alignment, variant, spacing, and outline,
 * enabling a diverse range of presentation styles. It dynamically adjusts the grid layout based on the number of teaser items.
 *
 * @param {CategoryTeaserType} data - The data object containing the layout configuration and an array of teaser items.
 * The layout configuration includes alignment (left, center), variant (circle, rectangle, type_only),
 * outer spacing (valid CSS margin values), and outline (none, outline_thin). The teaser items are an array of
 * individual paragraph data to be displayed within the teaser.
 * @returns A React component that renders a grid of CategoryTeaserItems, each with its own styling based on the
 * provided layout options and arranged according to the specified number of items per row.
 */
export const CategoryTeaser = ({
  data: {
    // Options: left, center, right
    behavior_paragraph_alignment = 'left',
    // Options: 'circle', 'reactangle', 'type_only'
    behavior_paragraph_variant: layoutVariant = 'reactangle',
    // Options: valid css margin values like: '0', '1rem', '1rem 2rem 3rem 4rem'
    behavior_paragraph_outer_spacing: layoutOuterSpacing = '0',
    // Options: 'none', 'outline_thin'
    behavior_paragraph_outline: layoutOutline = 'none',
    // Options: true, false | Note: Lazy loading should actually always be activated by default
    behavior_paragraph_lazy_load: isLazyLoading = true,
    // Custom identifier for dynamic yield content replacement
    behavior_paragraph_custom_id: customId,
    // Call-to-action
    behavior_paragraph_cta_icon,
    behavior_paragraph_cta_size,
    behavior_paragraph_cta_type_and_alignment,
    behavior_paragraph_columns_mobile,
    behavior_paragraph_columns_default,
    behavior_paragraph_layout,
    // teaser items
    paragraph: teaserItems,
    behavior_paragraph_borderRadius: borderRadius,
    id,
  },
  columnSizes,
}: {
  data: CategoryTeaserType;
  columnSizes?: string;
}) => {
  const isDesktop = useMediaType();
  const isVariantTypeOnly = layoutVariant === 'type_only';
  const isVariantCircle = layoutVariant === 'circle';

  const cellsPerRowMobile =
    behavior_paragraph_columns_mobile && Number.parseInt(behavior_paragraph_columns_mobile, 10);
  const cellsPerRowDefault =
    behavior_paragraph_columns_default && Number.parseInt(behavior_paragraph_columns_default, 10);
  const cellsPerRow =
    (!isDesktop && Number.isInteger(cellsPerRowMobile) && cellsPerRowMobile) ||
    (Number.isInteger(cellsPerRowDefault) && cellsPerRowDefault) ||
    (!isDesktop && isVariantTypeOnly && 1) ||
    (isDesktop && isVariantTypeOnly && 3) ||
    (!isDesktop && isVariantCircle && teaserItems?.length > 3 && 3) ||
    (!isDesktop && teaserItems?.length === 1 && 1) ||
    (!isDesktop && teaserItems?.length > 1 && 2) ||
    teaserItems?.length ||
    1;
  const colGap = '1.25rem';
  const rowGap = '1.25rem';
  const isGrid = cellsPerRow > 1;

  // Calculate grid-aware image sizes for teaser items
  const imageSizes = columnSizes
    ? `(min-width: 768px) calc(${columnSizes} / ${cellsPerRow}), calc(100vw / ${cellsPerRow})`
    : `(min-width: 768px) calc(100vw / ${cellsPerRow}), calc(100vw / ${cellsPerRow})`;

  return (
    <div
      className={classes(
        'flex flex-row items-start p-3 py-0 md:p-0',
        behavior_paragraph_alignment === 'center' && 'content-center text-center',
        behavior_paragraph_alignment === 'right' && 'content-right text-right',
        isVariantTypeOnly ? 'bg-color-text-light' : 'bg-color-transparent',
      )}
      style={{
        margin: layoutOuterSpacing,
      }}
      key={`categoryTeaser-${id}`}
      {...(customId && { 'data-dy': customId })}
    >
      <div className={classes('flex w-full flex-row flex-wrap', isVariantTypeOnly && 'max-w-none')}>
        {teaserItems?.map((item: CategoryTeaserItemType, index: number) => (
          <div
            key={item.id}
            className="size-full"
            style={{
              ...(isGrid && {
                flexBasis: `calc((100% - ${colGap} * (${cellsPerRow} - 1)) / ${cellsPerRow})`,
                [`&:nthOfType(${cellsPerRow}n+0)`]: {
                  marginRight: '0',
                },
              }),
              maxWidth: `calc(100% / ${cellsPerRow})`,
              marginBottom: rowGap,
              // only apply the margin if it is not the last item in the row
              marginRight: isGrid && (index + 1) % cellsPerRow ? colGap : 0,
            }}
          >
            <CategoryTeaserItem
              /* eslint-disable-next-line react/no-array-index-key */
              key={`category-teaser-item-${index}`}
              data={{
                ...item,
                behavior_paragraph_alignment,
                behavior_paragraph_cta_icon,
                behavior_paragraph_cta_size,
                behavior_paragraph_cta_type_and_alignment,
                behavior_paragraph_layout,
              }}
              layoutVariant={layoutVariant}
              layoutOutline={layoutOutline}
              borderRadius={borderRadius}
              isLazyLoading={isLazyLoading}
              imageSizes={imageSizes}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
